import { warn } from '@ember/debug';
import Component from '@glimmer/component';
import { addDays } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import DayCell from 'garaje/components/day-cell';
import type ActiveDayConfig from 'garaje/types/date-time/active-day-config';
import type DateLimits from 'garaje/types/date-time/date-limits';
import type DayConfig from 'garaje/types/date-time/day-config';
import { DATE_FNS_YYYY_MM_DD, isMoment, ensurePlainJsDate, DEFAULT_TIMEZONE } from 'garaje/utils/date-fns-tz-utilities';
import { type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';

interface WeekRowComponentSignature {
  Args: {
    currentMonth?: CalendarDate;
    startDate: CalendarDate;
    events?: CalendarDate[];
    hideLeadingDayCells?: boolean;
    hideTrailingDayCells?: boolean;
    respectSchedulingLimits?: boolean;
    selectedDates?: CalendarDate[];
    selectedEndDate?: CalendarDate;
    selectedStartDate?: CalendarDate;
    timezone?: string;
    selectDate?: (date: Date) => void;
    daysToCustomize?: Array<DayConfig>;
    dateLimits?: DateLimits;
    activeDays?: Array<ActiveDayConfig>;
  };
  Element: HTMLTableRowElement;
}

interface CalendarDay {
  date: CalendarDate;
  hasEvents: boolean;
}

export default class WeekRowComponent extends Component<WeekRowComponentSignature> {
  get timezone(): string {
    return this.args.timezone || DEFAULT_TIMEZONE;
  }

  get daysArray(): CalendarDay[] {
    const {
      timezone,
      args: { events },
    } = this;
    const startDate = ensurePlainJsDate(this.args.startDate, timezone);
    const eventsFormatted = (events || []).map((evt: CalendarDate): string => {
      warn('WeekRowComponent: @events uses an instance of Moment.js', !isMoment(evt), {
        id: 'envoy.week-row.moment-instance',
      });

      return formatInTimeZone(ensurePlainJsDate(evt, timezone), timezone, DATE_FNS_YYYY_MM_DD);
    });
    const days: CalendarDay[] = [];

    // Convert to zoned time once before adding days to handle DST correctly
    const zonedStartDate = toZonedTime(startDate, timezone);

    for (let i = 0; i < 7; i++) {
      const zonedDatePlusDays = addDays(zonedStartDate, i);
      const date = fromZonedTime(zonedDatePlusDays, timezone);
      const dateString = formatInTimeZone(date, timezone, DATE_FNS_YYYY_MM_DD);
      const hasEvents = eventsFormatted.includes(dateString);
      const config: CalendarDay = { date, hasEvents };

      days.push(config);
    }

    return days;
  }

  <template>
    <tr class="calendar-row" ...attributes>
      {{#each this.daysArray as |day idx|}}
        <DayCell
          data-test-day-cell={{idx}}
          @date={{day.date}}
          @currentMonth={{@currentMonth}}
          @selectedDates={{@selectedDates}}
          @selectedStartDate={{@selectedStartDate}}
          @selectedEndDate={{@selectedEndDate}}
          @respectSchedulingLimits={{@respectSchedulingLimits}}
          @hideTrailingDayCells={{@hideTrailingDayCells}}
          @hideLeadingDayCells={{@hideLeadingDayCells}}
          @hasEvents={{day.hasEvents}}
          @selectDate={{@selectDate}}
          @daysToCustomize={{@daysToCustomize}}
          @dateLimits={{@dateLimits}}
          @activeDays={{@activeDays}}
          @timezone={{this.timezone}}
        />
      {{/each}}
    </tr>
  </template>
}
